// @flow
import React, { useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";

import Gettext from "../common/Gettext";
import { rtl, fontSize } from "../common/styled-mixins";
import { useLocalStorage } from "../common/hooks";
import Info from "../Icons/Info";
import { useCompensations } from "./index";
import Popup, { Shields, comps } from "./Popup";
import { Modal } from "../common/Modal";
import CompensationPopup from "../TradeModals/CompensationPopup";

const Container = styled.div`
  width: 100%;
`;

const ProtectionContainer = styled.div`
  direction: ${({ theme }) => theme.direction};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ isLandscape }) =>
    isLandscape ? "flex-end" : "space-between"};
  margin-top: -16px;
  padding: 0 16px;
`;

const ProtectionText = styled.div`
  font-size: ${fontSize(14)};
  color: ${({ theme }) => theme.compensationSubtitle};
`;

const ShieldsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 21px;
  padding-top: 5px;

  ${({ isLandscape }) =>
    isLandscape &&
    css`
      justify-content: flex-start;
      padding-left: 16px;
      padding-right: 16px;
    `};

  > svg {
    ${rtl("margin-right", "4px")};

    :last-of-type {
      ${rtl("margin-right", 0)};
    }
  }
`;

const InfoIconContainer = styled.div`
  height: 16px;
  color: ${({ theme }) => theme.compensationInfoIcon};
`;

const DesktopShieldsContainer = styled(ShieldsContainer)`
  justify-content: flex-start;
  height: 16px;
  padding-top: 0;
`;

const DesktopContainer = styled.div`
  padding: 15px 28px 0 28px;
`;

const DesktopInfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  margin-top: 8px;
`;

const InfoIcon = props => {
  return (
    <Info
      width={16}
      height={16}
      style={{ cursor: "pointer", marginLeft: 3 }}
      onClick={props.onClick}
    />
  );
};

export const getStats = (compensations, typeAccount) => {
  if (compensations != null && compensations[typeAccount] && typeAccount) {
    return compensations[typeAccount].stats;
  }
};

type Props = {
  isDesktop?: boolean
};
const ProtectionInfo = React.memo((props: Props) => {
  const [showPopup, setShowPopup] = useState(false);
  const [showAllUsedPopup, setShowAllUsedPopup] = useState(false);

  const {
    compensationUpdated,
    compensationCreated,
    isDesktop,
    isLandscape,
    activeAccount
  } = props;

  const typeAccount = activeAccount && activeAccount.type;

  const compensations = useCompensations([
    compensationUpdated,
    compensationCreated
  ]);

  const availableCompensations = useMemo(
    () => comps(compensations, "available", typeAccount),
    [typeAccount, compensations]
  );

  const usedCompensations = useMemo(
    () => comps(compensations, "used", typeAccount),
    [typeAccount, compensations]
  );

  const compensationsStats = getStats(compensations, typeAccount);

  const [allUsedPopupIsShowed, setAllUsedPopupIsShowed] = useLocalStorage(
    "showed_all_used_compensations_popup",
    false
  );

  useEffect(() => {
    if (
      Number(availableCompensations) === 0 &&
      Number(usedCompensations) > 0 &&
      typeAccount === "demo" &&
      !allUsedPopupIsShowed
    ) {
      setShowAllUsedPopup(true);
      setAllUsedPopupIsShowed(true);
    }
  }, [availableCompensations]);

  if (
    compensations == null ||
    ((compensations.items && compensations.items.length === 0) ||
      (availableCompensations < 1 &&
        (typeAccount === "real" || showAllUsedPopup === false)))
  ) {
    return null;
  }

  return isDesktop ? (
    <DesktopContainer>
      <ProtectionText>
        <Gettext>Protection:</Gettext>
      </ProtectionText>
      <Modal mode={showAllUsedPopup}>
        <CompensationPopup
          availableCompensations={availableCompensations}
          usedCompensations={usedCompensations}
          profit={compensationsStats && compensationsStats.profit}
          compensated={compensationsStats && compensationsStats.compensated}
          onClose={() => setShowAllUsedPopup(false)}
          compensations={compensations}
        />
      </Modal>
      <DesktopInfoContainer>
        <DesktopShieldsContainer>
          <Shields
            availableCompensations={availableCompensations}
            usedCompensations={usedCompensations}
          />
        </DesktopShieldsContainer>
        <InfoIconContainer>
          <InfoIcon onClick={() => setShowPopup(true)} />
          <Modal mode={showPopup}>
            <Popup
              onDismiss={() => setShowPopup(false)}
              popupType="refill"
              compensations={compensations}
              isDesktop={isDesktop}
              isInfo={true}
              activeAccount={activeAccount}
            />
          </Modal>
        </InfoIconContainer>
      </DesktopInfoContainer>
    </DesktopContainer>
  ) : (
    <Container>
      <ShieldsContainer isLandscape={isLandscape}>
        <Shields
          availableCompensations={availableCompensations}
          usedCompensations={usedCompensations}
        />
      </ShieldsContainer>
      <ProtectionContainer isLandscape={isLandscape}>
        {!isLandscape && (
          <ProtectionText>
            <Gettext>Protection:</Gettext>
          </ProtectionText>
        )}
        <Modal mode={showAllUsedPopup}>
          <CompensationPopup
            availableCompensations={availableCompensations}
            usedCompensations={usedCompensations}
            profit={compensationsStats && compensationsStats.profit}
            compensated={compensationsStats && compensationsStats.compensated}
            onClose={() => setShowAllUsedPopup(false)}
            compensations={compensations}
          />
        </Modal>
        <InfoIconContainer>
          <InfoIcon onClick={() => setShowPopup(true)} />
          <Modal mode={showPopup}>
            <Popup
              onDismiss={() => setShowPopup(false)}
              popupType="refill"
              compensations={compensations}
              isDesktop={isDesktop}
              disableOutside={false}
              isInfo={true}
              activeAccount={activeAccount}
            />
          </Modal>
        </InfoIconContainer>
      </ProtectionContainer>
    </Container>
  );
});

export default ProtectionInfo;



// WEBPACK FOOTER //
// ./src/components/Protection/ProtectionInfo.js